<template>
    <div>
        <div v-if="memoId !== 'new' && memoId === null">
            <b-datatable
                :key="datatableKey"
                ref="grid"
                :columns="columns"
                :endpoint="listRoute"
                :hidden-columns="hiddenColumns"
                :initial-filters="initialFilters"
                initial-sort="-memo_date"
                :grid-title="t('patients::grid.title.memos')"
                has-filter-selector
                item-key="id"
            >
                <template #buttons>
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    v-if="can('api.v1.patients.memos.store')"
                                    color="primary"
                                    icon
                                    v-bind="attrs"
                                    @click="createMemo"
                                    v-on="on"
                                >
                                    <v-icon>mdi-plus-circle</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::button.create.new_memo') }}</span>
                        </v-tooltip>
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    v-if="can('patients.memos.export-as-csv')"
                                    color="primary"
                                    icon
                                    v-bind="attrs"
                                    @click="exportRecordAsCsv"
                                    v-on="on"
                                >
                                    <v-icon>mdi-file-export-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::button.export_csv') }}</span>
                        </v-tooltip>
                    </div>
                </template>
                <template #filters="{ filters }">
                    <v-col cols="4">
                        <b-filter-date
                            v-model="filters.filters.memo_date"
                            range
                            :label="t('patients::grid.label.memo_date')"
                            @clear="onCloseDateFilter(filters, 'memo_date')"
                        ></b-filter-date>
                    </v-col>
                    <v-col cols="4">
                        <b-filter-select
                            v-model="filters.filters.memo_type"
                            :items="filtersOptions.memo_type"
                            :label="t('patients::options.memo_type')"
                        >
                        </b-filter-select>
                    </v-col>
                    <v-col cols="4">
                        <b-filter-input
                            v-model="filters.filters.related_record_id"
                            :label="t('patients::options.related_record_id')"
                        >
                        </b-filter-input>
                    </v-col>
                </template>

                <template #[`item.memo_date`]="{ item }">
                    {{ item.memo_date ? moment(item.memo_date).format($root.dateFormat) : '' }}
                </template>

                <template #[`item.created_at`]="{ item }">
                    {{ item.created_at ? moment(item.created_at).format($root.dateFormat) : t('default::label.na') }}
                </template>

                <template #[`item.memo_text`]="{ item }">
                    <v-edit-dialog ref="memoDialog" :return-value.sync="item.memo_text" :transition="null">
                        {{ item.memo_text }}
                        <template #input>
                            <div class="close-action">
                                <v-icon color="accent" @click="closeEditDialog">mdi-close</v-icon>
                            </div>
                            <p>
                                {{ item.memo_text }}
                            </p>
                        </template>
                    </v-edit-dialog>
                </template>

                <template #[`item.memo_type`]="{ item }">
                    {{ t('patients::memos.types.' + item.memo_type) }}
                </template>

                <template #[`item.actions`]="{ item }">
                    <div class="table-actions">
                        <v-tooltip v-if="can('patients.memos.export-record-as-pdf')" top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="exportRecordAsPdf(item)"
                                >
                                    <v-icon size="24">mdi-file-export-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('default::button.export_record_as_pdf') }}</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="
                                can('api.v1.patients.memos.update') &&
                                item.manual &&
                                item.created_by.id === $store.state.user.profile.id
                            "
                            top
                            transition="v-fab-transition"
                        >
                            <template #activator="{ on, attrs }">
                                <v-btn color="disabled-color" icon v-bind="attrs" v-on="on" @click="editMemo(item)">
                                    <v-icon size="24">mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('default::button.edit') }}</span>
                        </v-tooltip>
                    </div>
                </template>
            </b-datatable>
        </div>
        <div v-else>
            <memo-form-component></memo-form-component>
        </div>
    </div>
</template>

<script>
export default {
    props: {},
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            listRoute: null,
            hiddenColumns: ['id', 'created_by.id'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.log_type'),
                    value: 'memo_type',
                    width: 150,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.log_text'),
                    value: 'memo_text',
                    width: 200,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.memo_date'),
                    value: 'memo_date',
                    width: 120,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.log_date'),
                    value: 'created_at',
                    width: 120,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.created_by'),
                    value: 'created_by.name',
                    width: 120,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.created_by'),
                    value: 'created_by.id',
                    width: 120,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.action'),
                    value: 'action',
                    width: 120,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'patient.id,manual',
                    width: 100,
                    align: 'end',
                },
            ],
            filtersOptions: {
                memo_type: [],
            },
            initialFilters: {
                memo_type: null,
            },
        };
    },
    computed: {
        patientId() {
            return this.$store.state.patients.patient.id;
        },
        tab() {
            return this.$store.state.patients.tab;
        },
        relatedRecordId() {
            return this.$store.state.patients.related_record_id;
        },
        memoId() {
            return this.$store.state.memo.memo.id;
        },
    },
    watch: {
        tab(tab) {
            if (tab === 5) {
                this.datatableKey++;
                this.$store.dispatch('memo/resetForm');
            }
        },
    },
    updated() {
        if (this.relatedRecordId && this.$refs.grid.filters) {
            this.$refs.grid.filters = Object.assign(
                { related_record_id: this.relatedRecordId },
                this.$refs.grid.filters
            );
        }
    },
    mounted() {
        this.datatableKey++;
    },
    created() {
        this.listRoute = route('api.v1.patients.memos.index', {
            patient: this.patientId,
        });

        axios.get(route('api.v1.memos.config')).then((response) => {
            this.filtersOptions.memo_type = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.memo_types && response.data.memo_types.length ? response.data.memo_types : []
            );
        });
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('patients.memos.export-record-as-pdf', { patient: item.patient.id, memo: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        async exportRecordAsCsv() {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('patients.memos.export-as-csv', { patient: this.patientId }), {
                    params: this.$refs.grid.filters,
                })
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'memo_date':
                    filters.filters.memo_date = null;
                    break;
                default:
                    break;
            }
        },
        closeEditDialog() {
            const event = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            this.$el.dispatchEvent(event);
        },
        createMemo() {
            return this.$store.dispatch('memo/newMemo');
        },
        editMemo(item) {
            return this.$store.dispatch('memo/loadMemo', item);
        },
    },
};
</script>

<style></style>
